import React, { useState } from "react";
import { Bar } from "react-chartjs-2";
import { Card, CardBody  } from "reactstrap";

import CardHeader from "../Layout/CardHeader";
import SuperInsightsModal from "../Ia/SuperInsightsModal";
import Meta from '../Layout/Meta';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  ArcElement,
  RadialLinearScale,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  ArcElement,
  RadialLinearScale
);

const BarChartX = ({ item }) => {

const safeItem = item || {};
  
let {
    Sigla = '',
    Descricao_Dashboard_Componente: titulo = "-",
    dadosComponente = {},  
    Valor_Meta = {},
    Texto_Info = "-",
    Texto_BizAI = "",
    ESG_Info = "-",
    Codigo_Transacao_Link = "",
    Titulo_Transacao_Link = "",
    opcoesComponente,
} = safeItem;

opcoesComponente = opcoesComponente || {};
const providedScales = opcoesComponente.scales || {};

// Normaliza dados: garante consistência entre labels e datasets e aplica defaults visuais para poucos pontos
const buildNormalizedData = () => {
  const labels = Array.isArray(dadosComponente?.labels) ? [...dadosComponente.labels] : [];
  const datasets = Array.isArray(dadosComponente?.datasets) ? dadosComponente.datasets : [];

  if (!datasets.length || !labels.length) {
    return { labels, datasets };
  }

  // Alinha comprimento entre labels e cada dataset.data
  const minLen = Math.min(labels.length, ...datasets.map(ds => Array.isArray(ds.data) ? ds.data.length : 0));
  const alignedLabels = labels.slice(0, minLen);

  // Normaliza números e aplica percentuais de barra para séries curtas
  const smallSeries = minLen < 4;
  const normalizedDatasets = datasets.map(ds => {
    const raw = Array.isArray(ds.data) ? ds.data.slice(0, minLen) : [];
    const data = raw.map(v => Number(v || 0));
    return {
      ...ds,
      data,
      categoryPercentage: ds.categoryPercentage ?? (smallSeries ? 0.6 : 0.8),
      barPercentage: ds.barPercentage ?? (smallSeries ? 0.5 : 0.8)
    };
  });

  // Remove pontos onde label é vazio e todos datasets são zero
  const validIdx = alignedLabels.map((lbl, i) => ({ i, lbl })).filter(({ i, lbl }) => {
    const anyValue = normalizedDatasets.some(ds => Number(ds.data?.[i] || 0) !== 0);
    return (lbl && String(lbl).trim().length > 0) || anyValue;
  }).map(({ i }) => i);

  const filteredLabels = validIdx.map(i => alignedLabels[i]);
  const filteredDatasets = normalizedDatasets.map(ds => ({
    ...ds,
    data: validIdx.map(i => Number(ds.data?.[i] || 0))
  }));

  return { labels: filteredLabels, datasets: filteredDatasets };
};

const chartData = buildNormalizedData();

const hasData = (() => {
  const anyValue = chartData.datasets.some(ds => (ds.data || []).some(v => Number(v || 0) !== 0));
  return (chartData.labels || []).length > 0 && anyValue;
})();

// Calcula step dinâmico (nice step) para o eixo Y quando não vier da API
const computeNiceStep = (range, targetTicks = 5) => {
  if (!isFinite(range) || range <= 0) return 1;
  const roughStep = range / targetTicks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const residual = roughStep / magnitude;
  if (residual >= 5) return 5 * magnitude;
  if (residual >= 2.5) return 2.5 * magnitude;
  if (residual >= 2) return 2 * magnitude;
  return 1 * magnitude;
};

const allValues = chartData.datasets.flatMap(ds => (Array.isArray(ds.data) ? ds.data : [])).map(Number).filter(v => isFinite(v));
const dataMin = allValues.length ? Math.min(...allValues) : 0;
const dataMax = allValues.length ? Math.max(...allValues) : 1;
const dataRange = Math.max(dataMax - Math.min(0, dataMin), 1);
const fallbackStep = computeNiceStep(dataRange, 5);
const suggestedMax = Math.ceil(dataMax / fallbackStep) * fallbackStep;

const formatBRLCompact = (value) => {
  const n = Number(value);
  if (!isFinite(n)) return value;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  const fmt = (den) => (abs / den).toLocaleString('pt-BR', {
    minimumFractionDigits: abs < den * 10 ? 1 : 0,
    maximumFractionDigits: abs < den * 10 ? 1 : 0
  });
  if (abs >= 1e9) return `${sign}R$ ${fmt(1e9)} bi`;
  if (abs >= 1e6) return `${sign}R$ ${fmt(1e6)} mi`;
  if (abs >= 1e3) return `${sign}R$ ${fmt(1e3)} mil`;
  return `${sign}R$ ${abs.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
};

const chartOptions = {
    ...opcoesComponente,
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      ...opcoesComponente.plugins,
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          font: {
            family: 'Poppins, sans-serif',
            size: 10,
            weight: '400',
            color: '#000'
          },
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#FFF',
        borderColor:   '#ccc',
        borderWidth:   1,
        titleColor:    '#666',
        titleFont: {
          family: 'Poppins, sans-serif',
          size: 12,
          weight: '700'
        },
        titleAlign: 'left',
        titleMarginBottom: 6,
        bodyColor:     '#000',
        displayColors: true,
        usePointStyle: true,
        font: {
          family: 'Poppins, sans-serif',
          weight: '600',   
          size: 9    
        },
        callbacks: {
          title: items => items[0].label,
          label: ctx => {
            const v = ctx.dataset.data[ctx.dataIndex];
            return `${ctx.dataset.label}: ${Number(v).toLocaleString('pt-BR',{
              style:'currency', currency:'BRL'
            })}`;
          },
          labelColor: ctx => ({
            borderColor:     'transparent',
            backgroundColor: ctx.dataset.backgroundColor,
            borderWidth:     0,
            width:           10,
            height:          10,
            borderRadius:    5
          })
        }
      }
    },

    scales: {
      ...providedScales,
      x: {
        ...providedScales.x,
        grid: {
          ...providedScales.x?.grid,
          display: false,
          drawBorder: false
        },
        border: {
          ...providedScales.x?.border,
          display: false
        },
        ticks: {
          ...(providedScales.x?.ticks || {}),
          font: {
            family: 'Poppins, sans-serif',
            weight: '600',   
            size: 9    
          }
        }
      },
      y: {
        ...providedScales.y,
        grid: {
          ...providedScales.y?.grid,
          drawBorder: false
        },
        border: {
          ...providedScales.y?.border,
          display: false
        },
        ticks: {
          ...(providedScales.y?.ticks || {}),
          // Fallbacks quando a API não especificar ticks
          count: providedScales.y?.ticks?.count ?? 5,
          stepSize: providedScales.y?.ticks?.stepSize ?? fallbackStep,
          min: providedScales.y?.ticks?.min ?? 0,
          max: providedScales.y?.ticks?.max ?? suggestedMax,
          font: {
            family: 'Poppins, sans-serif',
            size: 9
          },
          callback: val => formatBRLCompact(val)
        }
      }
    }
};


 const [isPopupOpen, setIsPopupOpen] = useState(false);  
 const togglePopup = () => setIsPopupOpen(!isPopupOpen);
 const slugId = Sigla.trim().replace(/\s/g, "-");
  
  return (
    <>
    
      <Card className="card-dash-item w-100 h-100">
        <CardBody className="d-flex flex-column">
          
            <CardHeader
              titulo={titulo}
              Texto_Info={Texto_Info}
              ESG_Info={ESG_Info}
              togglePopup={togglePopup}
              slugId={slugId}
              textoIA={Texto_BizAI}
              Codigo_Transacao_Link={Codigo_Transacao_Link}
              Titulo_Transacao_Link={Titulo_Transacao_Link}
            />
  
          <Meta Valor_Meta={Valor_Meta} />

          
          <div
            style={{
              marginTop: "6px",
              height: "100%" ,
            }}
          >
          
          {hasData ? (
            <Bar data={chartData} options={chartOptions} redraw={false} updateMode="none" />
          ) : (
            <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted" style={{ fontSize: 14 }}>
              Sem dados para exibir
            </div>
          )}
           
          </div>
      </CardBody>

    </Card>      
      <SuperInsightsModal
        isOpen={isPopupOpen}
        toggle={togglePopup}
        textoIA={Texto_BizAI} 
      />
    </>    
  );
};

export default BarChartX;
